import * as React from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export default function Tile({
  variant = "default",
  title,
  description,
  icon,
  children,
  className,
  action,
}: {
  variant?: "default" | "success" | "warning" | "info" | "accent";
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  // Brand-aligned tile backgrounds
  const variantBg: Record<string, string> = {
    default: "bg-card text-brand-text",
    success: "bg-brand-tile-success text-success",
    warning: "bg-brand-tile-warning text-amber-900",
    info: "bg-brand-tile-info text-sky-900",
    accent: "bg-brand-tile-accent text-violet-900",
  };

  // change the mapping above to adjust which CSS variables and text colors each Tile variant uses
  // - tile bg tokens are in client/src/index.css (look for --brand-tile-*)
  // - text color classes (e.g. text-success) are semantic utilities in client/src/index.css

  return (
    <Card
      className={cn(
        // Outer tile shell: rounded, padded, column layout so children can stretch
        "rounded-2xl p-6 shadow-sm flex flex-col transition-all duration-200 hover:shadow-md",
        "dark:text-slate-100",
        variantBg[variant],
        className
      )}
    >
      {/* Header section */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            {icon && (
              // Make icon background transparent (no inner white frame); icon color should inherit
              <div className="rounded-full p-2 bg-transparent">{icon}</div>
            )}
            {title && (
              <div className="sca-tile-title">{title}</div>
            )}
          </div>
          {description && (
            <div className="sca-tile-subtitle mt-1 text-slate-600 dark:text-slate-300">{description}</div>
          )}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>

      {/* Content area: flex-1 so footer/buttons inside can be pushed to bottom via mt-auto */}
      {children && <div className="mt-4 flex-1">{children}</div>}
    </Card>
  );
}